import { type JanusCtx, type Row, activeStatuses, definitionNotHeld, ec, ecId, ecLean, handBack, insertRoll, itemField, record, requiredInt, upper } from '../context.js';
import { type JanusInput } from '../janus.js';
import { postEffectConditions } from '../post-effect-conditions.js';
import { rollDiceFaces } from './roll-dice-arr.js';
import { effectTargets, isTargetValidForEffect } from '../is-target-valid-for-effect.js';
import { checkScalableCantrip } from '../check-scalable-cantrip.js';
import { upcastRoll } from '../upcast.js';
import { rollTypeModifierStacks } from '../skill-roll.js';
import { runTriggerFamily, triggerCall, triggerFamilyPreDraw } from '../trigger-executor.js';
import { collectTriggerCandidates, gateTriggerCandidate } from '../../reactions/trigger-dispatch.js';
import { janusCtxTriggerReaders } from '../../reactions/janus-ctx-trigger-readers.js';
import { fullDefinition } from '../../reactions/php-row-rules.js';
import { layerListEntryUuid } from '../statuses/status-trigger-gate.js';
import { removeStoredStatus } from '../statuses/status-effect-removal-helpers.js';
import { statusItemId } from '../../statuses/expiry.js';
import { abilityEntryId } from '../charges/charge-entry.js';
import { consentAllows } from '../trigger-consent.js';
import { setMana } from '../resource-reduction.js';

const INCREASE_MANA_ROLL = 'DB30C685-3830-9AEA-D8FA-D92FEA762982';
const INCREASE_LUCK_ROLL = 'C823C739-A9DE-AA5A-07B8-CB67F578F880';

/** Resource gains share the legacy per-target roll order and one caster-roll trigger frame. */
type Gain = { tag: string; rollType: string; column: 'mana' | 'luck'; cap: 'spirit' | 'fortune'; verb: 'restored' | 'gained' | 'spent'; reduce?: true };
const MANA: Gain = { tag: 'increase_mana_roll', rollType: INCREASE_MANA_ROLL, column: 'mana', cap: 'spirit', verb: 'restored' };
const LUCK: Gain = { tag: 'increase_luck_roll', rollType: INCREASE_LUCK_ROLL, column: 'luck', cap: 'fortune', verb: 'gained' };

const CMRT = 'triggers_when_caster_makes_roll_type';
const AT_ADV = 'triggers_if_roll_type_is_at_adv';
const SOURCE_BUCKETS: ReadonlySet<string> = new Set(['passive_ability', 'feat', 'reaction_ability']);
const HINT_AXES = ['is_rolled_at_max', 'is_rolled_at_min', 'roll_result_is_halved', 'roll_result_is_doubled'] as const;
type Hints = [boolean, boolean, boolean, boolean];
const lists = (ctx: JanusCtx, row: Row, key: string, type: string): boolean =>
  ctx.read.entries(row, key).some(entry => upper(layerListEntryUuid(entry)) === type);

/** Resolve real held definitions before either modifier lookup or a roll. Shells use the page's fetch/retry path. */
function statusSources(ctx: JanusCtx, holder: Row, g: Gain): { status: Row; definitions: Row[] }[] {
  return activeStatuses(ctx, holder).map(status => {
    const id = statusItemId(status);
    const definition = fullDefinition(record(status.status_arr), 'any') ?? fullDefinition(ctx.read.definition(id), 'any');
    if (!definition) definitionNotHeld(`${g.tag}:status-definition-not-held`, id);
    const definitions = [definition!];
    for (const entry of ctx.read.entries(definition!, 'passive_ability')) {
      const passiveId = layerListEntryUuid(entry);
      const passive = fullDefinition(ctx.read.definition(passiveId), 'any');
      if (!passive) definitionNotHeld(`${g.tag}:status-passive-definition-not-held`, passiveId);
      definitions.push(passive!);
    }
    return { status, definitions };
  });
}

function mergeHints(ctx: JanusCtx, hints: Hints, definition: Row, type: string, against = false): void {
  HINT_AXES.forEach((axis, i) => {
    hints[i] ||= lists(ctx, definition, `roll_type_${against ? 'against_' : ''}${axis}`, type)
      || (!against && lists(ctx, definition, `next_roll_type_${axis}`, type));
  });
}

/** A root roll-type list is not an executable carrier. The legacy family dispatch walks the holder's buckets and statuses. */
function carrierTriggers<T>(ctx: JanusCtx, caster: Row, run: () => T): T {
  const previous = ctx.read;
  ctx.read = { ...previous, has: (row, key) => row === caster && key === CMRT ? false : previous.has(row, key) };
  try { return run(); } finally { ctx.read = previous; }
}

function resourceStacks(ctx: JanusCtx, caster: Row, target: Row, ability: Row, g: Gain): number {
  const delta = (def: Row, against = false): number => Number(lists(ctx, def, `roll_type_${against ? 'against_' : ''}adv`, g.rollType))
    - Number(lists(ctx, def, `roll_type_${against ? 'against_' : ''}dis`, g.rollType));
  let stacks = delta(ability) + delta(caster) + delta(target, true);
  let conditionalStatusAdv = 0;
  const casterScope = { usedItemType: '', usedRollType: g.rollType, rollTo: ecId(target), opposing: target,
    reactionAllows: (definition: Row): boolean => {
      const id = upper(itemField(definition, 'item_id'));
      return !activeStatuses(ctx, caster).some(status => statusItemId(status) === id)
        && consentAllows(ctx, caster, definition, id, CMRT, 'reaction_ability');
    } };
  for (const [holder, opposing, against] of [[caster, target, false], [target, caster, true]] as const) {
    // The shared source walk's missing-definition reasons predate fetch/retry by UUID. Settle its references here with that identity.
    for (const bucket of SOURCE_BUCKETS) for (const entry of ctx.read.entries(holder, bucket)) {
      const inline = record(record(entry)?.item_arr);
      const id = abilityEntryId(entry);
      const definition = fullDefinition(inline, 'any') ?? fullDefinition(ctx.read.definition(id), 'any');
      if (!definition) definitionNotHeld(`${g.tag}:modifier-definition-not-held`, id);
      if (bucket === 'feat') for (const grant of ctx.read.entries(definition!, 'passive_ability')) {
        const grantId = layerListEntryUuid(grant);
        if (!fullDefinition(ctx.read.definition(grantId), 'any')) definitionNotHeld(`${g.tag}:modifier-definition-not-held`, grantId);
      }
    }
    stacks += rollTypeModifierStacks(ctx, holder, [g.rollType], against ? ['against_adv', 'against_dis'] : ['adv', 'dis'],
      against ? { usedItemType: '', usedRollType: g.rollType, rollTo: ecId(opposing), opposing } : casterScope, SOURCE_BUCKETS);
    for (const { status, definitions } of statusSources(ctx, holder, g)) {
      if (against && ctx.read.has(definitions[0]!, 'target_marked_by_caster')
          && upper(status.status_caster_id ?? status.caster) !== ecId(caster)) continue;
      for (const definition of definitions) {
        const conditional = !against && ctx.read.has(definition, AT_ADV)
          ? Number(lists(ctx, definition, 'roll_type_adv', g.rollType)) : 0;
        conditionalStatusAdv += conditional;
        stacks += delta(definition, against) - conditional;
      }
    }
  }
  if (stacks > 0) {
    // Reuse the skill/attack source walk and its gates for the deferred ADV axis without changing its shared, unconditional contract.
    const read = ctx.read;
    const conditionalCtx: JanusCtx = { ...ctx, read: { ...read, has: (row, key) => key !== AT_ADV && read.has(row, key)
      && (key !== 'roll_type_adv' || read.has(row, AT_ADV)) } };
    stacks += conditionalStatusAdv + rollTypeModifierStacks(conditionalCtx, caster, [g.rollType], ['adv'], casterScope, SOURCE_BUCKETS);
  }
  return Math.max(-5, Math.min(5, stacks));
}

function resourceGainRoll(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row, targets: Row[], g: Gain): void {
  const eligible = effectTargets(ctx, input, ability, caster, targets);
  if (!eligible) return;
  const rawDice = ctx.read.value(ability, 'dice');
  const rawDie = ctx.read.value(ability, 'die');
  let rawMod = ctx.read.value(ability, 'mod');
  if (rawMod === '[soul]') {
    rawMod = ctx.read.value(caster, 'soul');
    if (rawMod == null) handBack(`${g.tag}:soul-not-held`);
  }
  if (!/^\d+$/.test(String(rawDice)) || !/^\d+$/.test(String(rawDie))
      || (rawMod != null && !/^-?\d+$/.test(String(rawMod))))
    handBack(`${g.tag}:variable-dice-or-mod`);
  const baseDice = requiredInt(rawDice, `${g.tag}:invalid-dice`);
  const die = requiredInt(rawDie, `${g.tag}:invalid-die`);
  const up = upcastRoll(ctx, ability, baseDice, rawMod == null ? 0 : requiredInt(rawMod, `${g.tag}:invalid-mod`));
  const dice = checkScalableCantrip(ctx, ability, caster, up.dice), mod = up.mod;
  if (![dice, die].every(Number.isSafeInteger) || dice < 0 || dice > 100 || die < 0 || die > 1000 || (dice > 0 && die < 1))
    handBack('roll_dice_arr:invalid-arguments');
  const event = { trigger: CMRT, rollType: g.rollType };
  const candidates = ctx.trigger ? [] : triggerCall(() => {
    const { carriers, statuses } = collectTriggerCandidates(janusCtxTriggerReaders(ctx), caster, event);
    return [...carriers, ...statuses];
  });
  // Settle every target before the first draw, including dependencies of target-gated CMRT dispatches.
  statusSources(ctx, caster, g);
  for (const target of targets) {
    if (isTargetValidForEffect(ctx, ability, caster, target, target === targets[0]) !== 'proceed') continue;
    statusSources(ctx, target, g);
    resourceStacks(ctx, caster, target, ability, g);
    if (candidates.length) carrierTriggers(ctx, caster, () => triggerFamilyPreDraw(ctx, caster, event, undefined, target));
    requiredInt(ecLean(target, g.column), `${g.tag}:${g.column}-not-held`);
    requiredInt(ctx.read.value(target, g.cap) ?? ecLean(target, g.cap), `${g.tag}:${g.cap}-not-held`);
  }
  let fired = false;
  for (const target of eligible) {
    const advDisStacks = resourceStacks(ctx, caster, target, ability, g);
    const hints: Hints = [false, false, false, false];
    if (!fired && candidates.length) {
      const executed = carrierTriggers(ctx, caster, () => runTriggerFamily(ctx, event, caster, target, [caster], false));
      fired = executed.length > 0;
      for (const candidate of executed) mergeHints(ctx, hints, candidate.ability, g.rollType);
    } else if (fired) {
      // Pure passive hints are target-gated afresh; their effects/charges never dispatch a second time.
      for (const candidate of candidates) {
        if (candidate.carrier !== 'passive_ability' || itemField(candidate.ability, 'item_type') !== 'passive_ability'
            || !Object.keys(record(candidate.ability.item_layer_types) ?? {}).every(key => key === CMRT
              || /^(?:next_)?roll_type_(?:is_rolled_at_|roll_result_is_)/.test(key)
              || /^(?:if_target_has_status_effect_|requires_|unenchantable$|cost_gp$)/.test(key))) continue;
        if (triggerCall(() => gateTriggerCandidate(janusCtxTriggerReaders(ctx), candidate, caster, event, target)).fire)
          mergeHints(ctx, hints, candidate.ability, g.rollType);
      }
    }
    mergeHints(ctx, hints, ability, g.rollType);
    mergeHints(ctx, hints, caster, g.rollType);
    mergeHints(ctx, hints, target, g.rollType, true);
    const consumed: Row[] = [];
    for (const { status, definitions } of statusSources(ctx, caster, g)) {
      for (const definition of definitions) mergeHints(ctx, hints, definition, g.rollType);
      if (lists(ctx, definitions[0]!, 'triggers_on_next_roll_type', g.rollType)) consumed.push(status);
    }
    for (const { status, definitions } of statusSources(ctx, target, g)) {
      if (ctx.read.has(definitions[0]!, 'target_marked_by_caster')
          && upper(status.status_caster_id ?? status.caster) !== ecId(caster)) continue;
      for (const definition of definitions) mergeHints(ctx, hints, definition, g.rollType, true);
    }
    for (const status of consumed) if (activeStatuses(ctx, caster).includes(status)) removeStoredStatus(ctx, caster, status, 'single');
    const [max, min, halve, double] = hints;
    const forced = (max || min) && dice > 0 && die > 0 ? max ? die : 1 : null;
    const rolled = forced === null ? rollDiceFaces(ctx, dice, die, advDisStacks)
      : { total: dice * forced, faces: Array.from({ length: dice }, (_, i) => [forced, `d${die}_${i + 1} ${max ? 'Ω' : 'α'}`]) };
    const faces: unknown[] = [...rolled.faces];
    if (max) faces.push(['Ω', 'Roll Type is Rolled At Max']);
    else if (min) faces.push(['α', 'Roll Type is Rolled At Min']);
    let result = rolled.total + mod;
    if (halve) { result = Math.floor(result * .5); faces.push(['ξ', 'Roll Type Roll Result is Halved']); }
    if (double) { result *= 2; faces.push(['Γ', 'Roll Type Roll Result is Doubled']); }
    // PHP applies post effects before reseeding and updating the column.
    postEffectConditions(ctx, input, ability, caster, target);
    const before = requiredInt(ecLean(target, g.column), `${g.tag}:${g.column}-not-held`);
    const pool = requiredInt(ctx.read.value(target, g.cap) ?? ecLean(target, g.cap), `${g.tag}:${g.cap}-not-held`);
    const requested = g.reduce ? Math.max(0, before - result) : Math.min(before + result, pool);
    const after = g.column === 'mana' ? setMana(ctx, target, requested) : requested;
    if (g.column !== 'mana' && after !== before) ec(target)[g.column] = after;
    // the row's roll_feedback (PHP: rolled, the cap-aware gain, 'restored' / 'gained', 'already full' when the pool was full — Codex A7 review 2)
    insertRoll(ctx, { roll_type_1: g.rollType, roll_type_2: '', roll_item_id: input.abilityId,
      by: caster, to: target, roll_result: result, rolls_full: faces,
      dice, die, mod, adv_dis_stacks: advDisStacks, dc: 0,
      feedback: { rolled: result, applied: Math.abs(after - before), verb: g.verb,
        reason: !g.reduce && before >= pool ? 'already full' : null } });
  }
}

/** includes/functions/janus/rolls/increase_mana_roll.php:increase_mana_roll */
export function increaseManaRoll(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row, targets: Row[]): void {
  resourceGainRoll(ctx, input, ability, caster, targets, MANA);
}
export function reduceManaRoll(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row, targets: Row[]): void {
  resourceGainRoll(ctx, input, ability, caster, targets, { ...MANA, tag: 'reduce_mana_roll',
    rollType: '905613F5-A045-BC0A-3686-CD1EC15060C5', verb: 'spent', reduce: true });
}
/** includes/functions/janus/rolls/increase_luck_roll.php:increase_luck_roll */
export function increaseLuckRoll(ctx: JanusCtx, input: JanusInput, ability: Row, caster: Row, targets: Row[]): void {
  resourceGainRoll(ctx, input, ability, caster, targets, LUCK);
}
